import type { Field, Trigger } from '@evidia/components';
import type { DrivingLessonRecordState } from '@evidia/shared';
import { DrivingLessonRecordType } from '@evidia/shared';
import dayjs from 'dayjs';
import {
	RULE_DRIVING_LESSON_START_TIME,
	RULE_DRIVING_LESSON_TACHOMETER_AFTER,
	RULE_DRIVING_LESSON_TACHOMETER_BEFORE,
} from '../../../../const/forms';
import { useActiveSchoolId } from '../../../../hooks/useActiveSchoolId';
import { mergeDateAndTime } from '../../../../utils/date';
import { getDrivingLessonRecordTypeName } from '../../../../utils/naming';
import { trpc } from '../../../../utils/trcp';
import { CollisionError } from './CollisionError';
import SignatureItem from './SignatureItem';

export const CAR_RESERVATION_KEY = 'CAR_RESERVATION';

export const CALENDAR_TYPE = [
	{
		key: CAR_RESERVATION_KEY,
		value: 'Blokace vozidla',
	},
];

const TRIGGERS: Trigger = {
	car: (setFormValues, newValue, allValues: { startTimeDate?: Date; startTimeTime?: Date }) => {
		setFormValues({
			// force rerender error component
			error: allValues,
		});
	},
	teacher: (setFormValues, newValue, allValues: { startTimeDate?: Date; startTimeTime?: Date }) => {
		setFormValues({
			// force rerender error component
			error: allValues,
		});
	},
	length: (setFormValues, newValue, allValues: { startTimeDate?: Date; startTimeTime?: Date }) => {
		if (!allValues.startTimeDate || !allValues.startTimeTime) {
			return;
		}
		const startTime = dayjs(mergeDateAndTime(allValues.startTimeDate, allValues.startTimeTime));
		const endTime = startTime.add(parseInt(newValue, 10), 'minute');

		setFormValues({
			endTimeDate: endTime.toDate(),
			endTimeTime: endTime.toDate(),
			// force rerender error component
			error: allValues,
		});
	},
	startTimeDate: (
		setFormValues,
		newValue,
		allValues: { startTimeDate?: Date; startTimeTime?: Date; length?: string; type?: DrivingLessonRecordType },
	) => {
		if (allValues.type === DrivingLessonRecordType.DRIVING) {
			if (!allValues.startTimeTime || !allValues.length) {
				return;
			}
			const startTime = dayjs(mergeDateAndTime(newValue, allValues.startTimeTime));
			const endTime = startTime.add(parseInt(allValues.length, 10), 'minute');

			setFormValues({
				endTimeDate: endTime.toDate(),
				endTimeTime: endTime.toDate(),
				// force rerender error component
				error: allValues,
			});
			return;
		}

		if (allValues.startTimeDate) {
			setFormValues({
				endTimeDate: allValues.startTimeDate,
			});
		}
	},
	startTimeTime: (
		setFormValues,
		newValue,
		allValues: {
			startTimeDate?: Date;
			startTimeTime?: Date;
			length?: string;
			type?: DrivingLessonRecordType | typeof CAR_RESERVATION_KEY;
		},
	) => {
		if (allValues.type === DrivingLessonRecordType.DRIVING) {
			if (!allValues.startTimeDate || !allValues.length) {
				return;
			}
			const startTime = dayjs(mergeDateAndTime(allValues.startTimeDate, newValue));
			const endTime = startTime.add(parseInt(allValues.length, 10), 'minute');

			setFormValues({
				endTimeDate: endTime.toDate(),
				endTimeTime: endTime.toDate(),
				// force rerender error component
				error: allValues,
			});

			return;
		}

		if (allValues.startTimeTime && allValues.type !== CAR_RESERVATION_KEY) {
			setFormValues({
				endTimeTime: dayjs(allValues.startTimeTime).add(1, 'hour').toDate(),
			});
		}
	},
	endTimeDate: (setFormValues, newValue, allValues: { endTimeDate?: Date; endTimeTime?: Date }) => {
		setFormValues({
			// force rerender error component
			error: allValues,
		});
	},
	endTimeTime: (setFormValues, newValue, allValues: { endTimeDate?: Date; endTimeTime?: Date }) => {
		setFormValues({
			// force rerender error component
			error: allValues,
		});
	},
	allDayCarReservationEvent: (setFormValues, newValue, allValues: { allDayCarReservationEvent?: boolean }) => {
		if (allValues.allDayCarReservationEvent) {
			const now = dayjs();
			setFormValues({
				startTimeDate: now.startOf('day').toDate(),
				startTimeTime: now.startOf('day').toDate(),

				endTimeDate: now.endOf('day').toDate(),
				endTimeTime: now.endOf('day').toDate(),
				// force rerender error component
				error: allValues,
			});
		}
	},
};

export const STATE_MAP: Record<DrivingLessonRecordState, string> = {
	SCHEDULED: 'Naplánováno',
	COMPLETED: 'Absolvována',
	CANCELLED_BY_STUDENT: 'Zrušena žákem',
	CANCELLED_BY_TEACHER: 'Zrušena autoškolou',
};

interface Options {
	type: 'create' | 'plan';
	referer: 'studen-card' | 'calendar';
	editId?: string;
}

const useTrainingFields = (options: Options) => {
	const { type, referer } = options;

	const isCalendar = referer === 'calendar';
	const { activeSchoolId } = useActiveSchoolId();
	const getSchoolTeachers = trpc.school.getSchoolTeachers.useQuery({ schoolId: activeSchoolId });
	const getSchoolCar = trpc.schoolCar.getSchoolCar.useQuery({ schoolId: activeSchoolId });
	const getStudentsForTrainingFields = trpc.school.getStudentsForTrainingFields.useQuery(
		{
			schoolId: activeSchoolId,
		},
		{
			enabled: isCalendar,
		},
	);

	const trainingFieldsMap = [
		['type', 'type', 'type'],
		['allDayCarReservationEvent', 'allDayCarReservationEvent', 'allDayCarReservationEvent'],
		['startTimeDate', 'startTimeDate', 'startTimeTime'],
		['length', 'length', 'length'],
		['endTimeDate', 'endTimeDate', 'endTimeTime'],
		['teacher', 'teacher', 'teacher'],
		['car', 'car', 'car'],
		['note', 'note', 'note'],
		['student', 'student', 'student'],
	];

	const teachers = getSchoolTeachers.data?.map(teacher => ({
		key: teacher.id,
		value: `${teacher.firstName} ${teacher.lastName}`,
	}));
	const cars = getSchoolCar.data?.map(car => ({
		key: car.id,
		value: `${car.carType} (${car.regNumber})`,
	}));

	const students = getStudentsForTrainingFields.data?.map(student => ({
		key: student.id,
		value: `${student.studentInfo?.firstName} ${student.studentInfo?.lastName}`,
	}));

	const fields: Field[] = [
		{
			key: 'type',
			required: true,
			type: 'select',
			label: isCalendar ? 'Typ události' : 'Typ výcviku',
			values: [
				{
					key: DrivingLessonRecordType.DRIVING,
					value: getDrivingLessonRecordTypeName(DrivingLessonRecordType.DRIVING),
				},
				{
					key: DrivingLessonRecordType.PV_UV,
					value: getDrivingLessonRecordTypeName(DrivingLessonRecordType.PV_UV),
				},
				{
					key: DrivingLessonRecordType.PV_ZP,
					value: getDrivingLessonRecordTypeName(DrivingLessonRecordType.PV_ZP),
				},
				...(isCalendar ? CALENDAR_TYPE : []),
			],
		},
		{
			key: 'startTimeDate',
			required: true,
			label: 'Začátek výcviku',
			type: 'day-picker',
			rules: [RULE_DRIVING_LESSON_START_TIME('startTimeDate')],
			showWhen: {
				type: [DrivingLessonRecordType.DRIVING, DrivingLessonRecordType.PV_ZP, DrivingLessonRecordType.PV_UV],
			},
		},
		{
			key: 'startTimeTime',
			required: true,
			label: ' ',
			type: 'time-picker',
			rules: [RULE_DRIVING_LESSON_START_TIME('startTimeTime')],
			showWhen: {
				type: [DrivingLessonRecordType.DRIVING, DrivingLessonRecordType.PV_ZP, DrivingLessonRecordType.PV_UV],
			},
		},
		{
			key: 'allDayCarReservationEvent',
			label: 'Celý den',
			type: 'checkbox',
			node: <div>Celodenní blokace</div>,
			required: false,
			showWhen: {
				type: CAR_RESERVATION_KEY,
			},
		},
		{
			key: 'startTimeDate',
			required: true,
			label: 'Začátek blokace',
			type: 'day-picker',
			rules: [RULE_DRIVING_LESSON_START_TIME('startTimeDate')],
			showWhen: {
				type: [CAR_RESERVATION_KEY],
			},
		},
		{
			key: 'startTimeTime',
			required: true,
			label: ' ',
			type: 'time-picker',
			rules: [RULE_DRIVING_LESSON_START_TIME('startTimeTime')],
			showWhen: {
				type: [CAR_RESERVATION_KEY],
			},
		},
		{
			key: 'length',
			required: true,
			label: 'Délka jízdy',
			type: 'button-select',
			values: [
				{ key: '45', value: '45 min.' },
				{ key: '60', value: '60 min.' },
				{ key: '90', value: '90 min.' },
				{ key: '120', value: '120 min.' },
			],
			showWhen: {
				type: DrivingLessonRecordType.DRIVING,
			},
		},
		//
		{
			disabled: true, // type === 'create',
			key: 'endTimeDate',
			required: true,
			label: 'Konec výcviku',
			type: 'day-picker',
			rules: [RULE_DRIVING_LESSON_START_TIME('endTimeDate')],
			showWhen: {
				type: DrivingLessonRecordType.DRIVING,
			},
		},
		{
			key: 'endTimeDate',
			required: true,
			label: 'Konec výcviku',
			type: 'day-picker',
			rules: [RULE_DRIVING_LESSON_START_TIME('endTimeDate')],
			showWhen: {
				type: [DrivingLessonRecordType.PV_ZP, DrivingLessonRecordType.PV_UV],
			},
		},
		{
			key: 'endTimeDate',
			required: true,
			label: 'Konec blokace',
			type: 'day-picker',
			rules: [RULE_DRIVING_LESSON_START_TIME('endTimeDate')],
			showWhen: {
				type: CAR_RESERVATION_KEY,
			},
		},
		//
		{
			disabled: true, // type === 'create',
			key: 'endTimeTime',
			required: true,
			label: ' ',
			type: 'time-picker',
			rules: [RULE_DRIVING_LESSON_START_TIME('endTimeTime')],
			showWhen: {
				type: DrivingLessonRecordType.DRIVING,
			},
		},
		{
			key: 'endTimeTime',
			required: true,
			label: ' ',
			type: 'time-picker',
			rules: [RULE_DRIVING_LESSON_START_TIME('endTimeTime')],
			showWhen: {
				type: [DrivingLessonRecordType.PV_ZP, DrivingLessonRecordType.PV_UV],
			},
		},
		{
			key: 'endTimeTime',
			required: true,
			label: ' ',
			type: 'time-picker',
			rules: [RULE_DRIVING_LESSON_START_TIME('endTimeTime')],
			showWhen: {
				type: CAR_RESERVATION_KEY,
			},
		},
		//
		{
			key: 'teacher',
			required: true,
			label: 'Učitel',
			type: 'select',
			loading: getSchoolTeachers.isLoading,
			values: [{ key: '__empty', value: 'Nevybráno' }, ...(teachers ?? [])],
			showWhen: {
				type: [DrivingLessonRecordType.DRIVING, DrivingLessonRecordType.PV_ZP, DrivingLessonRecordType.PV_UV],
			},
		},
		{
			key: 'car',
			required: true,
			label: 'Vozidlo',
			type: 'select',
			loading: getSchoolCar.isLoading,
			values: [{ key: '__empty', value: 'Nevybráno' }, ...(cars ?? [])],
			showWhen: {
				type: [DrivingLessonRecordType.DRIVING, CAR_RESERVATION_KEY],
			},
		},
		{
			key: 'note',
			label: 'Poznámka',
			type: 'string',
			required: false,
			showWhen: {
				type: CAR_RESERVATION_KEY,
			},
		},
	];

	const isEdit = options.editId !== undefined;

	if (type === 'create' || isEdit) {
		fields.push({
			key: 'tachometerBefore',
			label: 'Tachometr před jízdou',
			type: 'number',
			required: false,
			suffix: 'Km',
			rules: [RULE_DRIVING_LESSON_TACHOMETER_BEFORE],
			showWhen: {
				type: DrivingLessonRecordType.DRIVING,
			},
		});
		fields.push({
			key: 'tachometerAfter',
			label: 'Tachometr po jízdě',
			type: 'number',
			required: false,
			suffix: 'Km',
			rules: [RULE_DRIVING_LESSON_TACHOMETER_AFTER],
			showWhen: {
				type: DrivingLessonRecordType.DRIVING,
			},
		});
		fields.push({
			key: 'signature',
			label: 'Podpis žáka',
			required: true,
			type: 'custom',
			render: () => <SignatureItem title="Podpis žáka" buttonText="Podepsat výcvik - žák" />,
			showWhen: {
				type: [DrivingLessonRecordType.DRIVING, DrivingLessonRecordType.PV_ZP, DrivingLessonRecordType.PV_UV],
			},
		});
		fields.push({
			key: 'teacherSignature',
			label: 'Podpis učitele',
			required: true,
			type: 'custom',
			render: () => <SignatureItem title="Podpis učitele" buttonText="Podepsat výcvik - učitel" />,
			showWhen: {
				type: [DrivingLessonRecordType.DRIVING, DrivingLessonRecordType.PV_ZP, DrivingLessonRecordType.PV_UV],
			},
		});

		trainingFieldsMap.push(['tachometerBefore', 'tachometerBefore', 'tachometerBefore']);
		trainingFieldsMap.push(['tachometerAfter', 'tachometerAfter', 'tachometerAfter']);
		trainingFieldsMap.push(['signature', 'signature', 'signature']);
		trainingFieldsMap.push(['teacherSignature', 'teacherSignature', 'teacherSignature']);
	}

	if (isEdit) {
		fields.push({
			key: 'state',
			label: 'Stav',
			type: 'select',
			required: true,
			values: Object.keys(STATE_MAP).map(key => ({
				key,
				value: STATE_MAP[key as DrivingLessonRecordState],
			})),
			showWhen: {
				type: v => v !== CAR_RESERVATION_KEY,
			},
		});

		trainingFieldsMap.push(['state', 'state', 'state']);
	}

	if (isCalendar) {
		fields.push({
			key: 'student',
			label: 'Žák',
			type: 'select',
			required: true,
			values: [{ key: '__empty', value: 'Nevybráno' }, ...(students ?? [])],
			showWhen: {
				type: v =>
					v !== CAR_RESERVATION_KEY &&
					[
						DrivingLessonRecordType.DRIVING,
						DrivingLessonRecordType.PV_ZP,
						DrivingLessonRecordType.PV_UV,
					].includes(v),
			},
		});
	}

	trainingFieldsMap.push(['error', 'error', 'error']);
	fields.push({
		key: 'error',
		label: '',
		type: 'custom',
		render: (value, _, optns) => (
			<CollisionError
				value={value}
				isFormDisabled={optns?.isFormDisabled}
				setFormDisabled={optns?.setFormDisabled}
				editId={options.editId}
			/>
		),
		required: false,
	});

	return { fields, trainingFieldsMap, triggers: TRIGGERS };
};
export default useTrainingFields;
